import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { Box, useMediaQuery, useTheme, IconButton } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import Sidebar from "./Components/Sidebar";
import LoginForm from "./Components/LoginForm";
import Dashboard from "./Pages/Dashboard";
import Roles from "./Pages/Roles";
import UserRoles from "./Pages/UserRoles";
import Oraganization from "./Pages/Organization";
import Department from "./Pages/Department";
import ClientProjects from "./Pages/ClientProjects";
import EmployeeProjects from "./Pages/EmployeeProjects";
import Timesheet from "./Pages/Timesheets";
import TimesheetTracker from "./Pages/TimesheetTracker";
import Payslip from "./Pages/Payslips";
import LeaveRequest from "./Pages/LeaveRequest";
import AssetDetails from "./Pages/AssetDetails";
import EmployeeAssets from "./Pages/EmployeeAssets";
import Clients from "./Pages/Clients";
import EmployeeSalary from "./Pages/EmployeeSalaries";
import Employees from "./Pages/Employees";
import Assets from "./Pages/Assets";
import EmployeeDetails from "./Pages/EmployeeDetails";
import LeaveApproval from "./Pages/LeaveApproval";
import ProtectedRoute from "./utils/ProtectedRoute";
import Unauthorized from "./Pages/Unauthorized";
import EmployeeAttendance from "./Pages/EmployeeAttendance";
import Users from "./Pages/Users";
import Screens from "./Pages/Screens";
import Groups from "./Pages/Groups";
import Permissions from "./Pages/permissions";
import Profile from "./Pages/Profile";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const App = () => {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isLoginPage = location.pathname === "/login" || location.pathname === "/";
  const [isAuthenticated, setIsAuthenticated] = useState(null); // null = loading
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  // Check session on mount
  useEffect(() => {
    const checkSession = async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/me`, {
          credentials: "include",
        });
        if (res.ok) {
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
        }
      } catch {
        setIsAuthenticated(false);
      }
    };
    checkSession();
  }, []);
  // Handle sidebar toggle for mobile view
  const handleSidebarToggle = () => setMobileOpen((prev) => !prev);
  if (isAuthenticated === null) return <div>Loading...</div>; // while checking cookie
  return (
    <Box sx={{ display: "flex", height: "100vh", overflow: "hidden" }}>
      {!isLoginPage && (
        <Sidebar
          mobileOpen={mobileOpen}
          onToggle={handleSidebarToggle}
          isMobile={isMobile}
        />
      )}

      <Box
        component="main"
        sx={{
          flex: 1,
          overflow: "auto",
          p: { xs: 2, sm: 3, md: 4 },
          mt: { xs: "64px", md: "80px" },
          ml: !isLoginPage && !isMobile && mobileOpen ? "80px" : 0,
          transition: "margin-left 0.3s ease",
        }}
      >
        {!isLoginPage && (
          <IconButton
            onClick={handleSidebarToggle}
            sx={{
              position: "absolute",
              top: 16,
              left: 16,
              zIndex: 1301,
              backgroundColor: "#fff",
              borderRadius: "50%",
              boxShadow: 1,
              display: { xs: "block", md: "none" } // Only show on mobile
            }}
          >
            <MenuIcon />
          </IconButton>
        )}
        <Routes>
          <Route
            path="/login"
            element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginForm />}
          />
          <Route
            path="/dashboard"
            element={<ProtectedRoute screen="Dashboard" action="View"><Dashboard /></ProtectedRoute>}
          />
          <Route
            path="/roles"
            element={<ProtectedRoute screen="Roles" action="View"><Roles /></ProtectedRoute>}
          />
          <Route
            path="/userroles"
            element={<ProtectedRoute screen="User Roles" action="View"><UserRoles /></ProtectedRoute>}
          />
          <Route
            path="/department"
            element={<ProtectedRoute screen="Department" action="View"><Department /></ProtectedRoute>}
          />
          <Route
            path="/organization"
            element={<ProtectedRoute screen="Organization" action="View"><Oraganization /></ProtectedRoute>}
          />
          <Route
            path="/clients"
            element={<ProtectedRoute screen="Clients" action="View"><Clients /></ProtectedRoute>}
          />
          <Route
            path="/clientprojects"
            element={<ProtectedRoute screen="Client Projects" action="View"><ClientProjects /></ProtectedRoute>}
          />
          <Route
            path="/assets"
            element={<ProtectedRoute screen="Assets" action="View"><Assets /></ProtectedRoute>}
          />
          <Route
            path="/employeeprojects"
            element={<ProtectedRoute screen="Employee Projects" action="View"><EmployeeProjects /></ProtectedRoute>}
          />
          <Route
            path="/timesheet"
            element={<ProtectedRoute screen="Timesheet" action="View"><Timesheet /></ProtectedRoute>}
          />
          <Route
            path="/timesheettracker"
            element={<ProtectedRoute screen="Timesheet Approval" action="View"><TimesheetTracker /></ProtectedRoute>}
          />
          <Route
            path="/payslip"
            element={<ProtectedRoute screen="Payslip" action="View"><Payslip /></ProtectedRoute>}
          />
          <Route
            path="/leaverequest"
            element={<ProtectedRoute screen="Leave Request" action="View"><LeaveRequest /></ProtectedRoute>}
          />
          <Route
            path="/employeeassets"
            element={<ProtectedRoute screen="Employee Assets" action="View"><EmployeeAssets /></ProtectedRoute>}
          />
          <Route
            path="/employeesalaries"
            element={<ProtectedRoute screen="Employee Salaries" action="View"><EmployeeSalary /></ProtectedRoute>}
          />
          <Route
            path="/employeedetails"
            element={<ProtectedRoute screen="Employees" action="View"><EmployeeDetails /></ProtectedRoute>}
          />
          <Route
            path="/leaveapproval"
            element={<ProtectedRoute screen="Leave Approval" action="View">< LeaveApproval /></ProtectedRoute>}
          />
          <Route
            path="/assetdetails"
            element={<ProtectedRoute screen="Assets" action="View"><AssetDetails /></ProtectedRoute>}
          />
          <Route
            path="/employeeattendance"
            element={<ProtectedRoute screen="Check In Out" action="View"><EmployeeAttendance /></ProtectedRoute>} />
          <Route
            path="/users"
            element={<ProtectedRoute screen="Users" action="View"><Users /></ProtectedRoute>}
          />
          <Route
            path="/screens"
            element={<ProtectedRoute screen="Screens" action="View"><Screens /></ProtectedRoute>}
          />
          <Route
            path="/groups"
            element={<ProtectedRoute screen="Groups" action="View"><Groups /></ProtectedRoute>}
          />
          <Route
            path="/permissions"
            element={<ProtectedRoute screen="Permissions" action="View"><Permissions /></ProtectedRoute>}
          />
          <Route path="/profile" element={<Profile />} />
          <Route path="/unauthorized" element={<Unauthorized />} />
          <Route path="/" element={<Navigate to="/login" replace />} />
        </Routes>
      </Box>
    </Box>
  );
};

const AppWithRouter = () => (
  <Router>
    <App />
  </Router>
);

export default AppWithRouter;